import { RedirectRequest, SilentRequest } from "@azure/msal-browser";
import publicClientApplication from "./msalPublicClientApplication";

const getAccessTokenRequestWithScopes = (scopes: string[]) => {
  const account = publicClientApplication.instance().getAllAccounts()[0];
  const accessTokenRequest: SilentRequest = {
    scopes,
    account: account,
  };
  return accessTokenRequest;
};

export interface TokenProvider {
  getAccessToken: () => Promise<string>;
  getAccessTokenRequest: () => RedirectRequest;
  getAuthHeaders: () => Promise<Record<string, string>>;
}

export const tokenProviderFactory = (scopes: string[]): TokenProvider => {
  const getAccessTokenRequest = () => {
    return getAccessTokenRequestWithScopes(scopes);
  };

  const getAccessToken = async () => {
    const activeAccount = publicClientApplication.instance().getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
    const accounts = publicClientApplication.instance().getAllAccounts();

    if (!activeAccount && accounts.length === 0) {
      /*
      * User is not signed in. Throw error or wait for user to login.
      * Do not attempt to log a user in outside of the context of MsalProvider
      */
     return;
    }

    const accessTokenRequest = getAccessTokenRequest();

    try {
      const token = await publicClientApplication.instance().acquireTokenSilent(accessTokenRequest);
      return token.accessToken;
    } catch (e) {

      // Check multiple properties where the error might be stored
      const errorCode = e?.errorCode || e?.error || '';
      const errorMessage = e?.message || e?.errorMessage || e?.error_description || '';
      const subError = e?.subError || '';

      // Combine all error information for checking
      const fullErrorString = `${errorCode} ${errorMessage} ${subError} ${String(e)}`.toLowerCase();

      const requiresReauth = fullErrorString.includes('invalid_grant') ||
                            fullErrorString.includes('invalid grant') ||
                            fullErrorString.includes('monitor_window_timeout') ||
                            fullErrorString.includes('interaction_required');

      if (requiresReauth) {
        console.log("Authentication error detected, redirecting to login");
        await publicClientApplication.instance().acquireTokenRedirect(accessTokenRequest);
        return new Promise(() => {}) as Promise<string>;
      }
      throw e;
    }
  };

  const getAuthHeaders = async () => {
    const token = await getAccessToken();
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  return {
    getAccessToken,
    getAccessTokenRequest,
    getAuthHeaders,
  };
};
